import { Box, Spinner } from "@chakra-ui/react";
import { default as IconPinClass } from "assets/IconPinClass.svg";
import useStore from "stores/index";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import shallow from "zustand/shallow";
import { EDIT_CLASSROOM_ACTION } from "./ClassroomTileV2";
import ErrorBoundary from "@/components/ErrorBoundary";
import { PrimaryButton } from "./shared/button/PrimaryButton";
import Checkbox from "./shared/Checkbox";
import StandardModalV2 from "./shared/StandardModalV2";
import { getClassroomDisplayName, getClassroomOriginalName } from "@/utils/classroomUtils";

const ClassroomRename = ({ classroom, open, handleAction }) => {
	const { t } = useTranslation();
	const [hasError, setHasError] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [stagedPersonalisation, setPersonalisation] = useState(classroom.personalisation);
	const [displayName, setDisplayName] = useState("");

	const classroomStore = useStore(
		(state) => ({
			validateClassroomName: state.classroom.validateClassroomName,
			setClassroomName: state.classroom.setClassroomName,
		}),
		shallow
	);

	useEffect(() => {
		setPersonalisation(classroom.personalisation);
	}, [classroom.personalisation]);

	const renderActions = () => {
		const renderSave = () => {
			if (isSaving) {
				return (
					<ErrorBoundary>
						<PrimaryButton className="mui-flat-button" label="" primary={true}>
							<Spinner color="white" width={"12px"} height={"12px"} />
						</PrimaryButton>
					</ErrorBoundary>
				);
			}
			const disabled = !(displayName && displayName.length > 0);
			return (
				<PrimaryButton
					className="mui-flat-button"
					label={t("classRename.save")}
					primary={true}
					onClick={() => handleEditAction(EDIT_CLASSROOM_ACTION.save)}
					data-cy="save"
					disabled={disabled}
				/>
			);
		};

		return <div className="ClassroomEdit_modalButtons">{renderSave()}</div>;
	};

	const handleSave = async (event) => {
		const { personalisation, id } = classroom;
		if (personalisation?.pinned !== stagedPersonalisation?.pinned || personalisation?.displayName !== stagedPersonalisation?.displayName) {
			setIsSaving(true);
			if (event) event.preventDefault();

			if (stagedPersonalisation.displayName.trim().length > 50) {
				setHasError(t("classRename.maxCharsError"));
				setIsSaving(false);
				return;
			}

			if (!classroomStore.validateClassroomName(stagedPersonalisation.displayName.toLowerCase(), classroom.id)) {
				await classroomStore
					.setClassroomName(id, { ...stagedPersonalisation, displayName: stagedPersonalisation.displayName.trim() })
					.then(() => {
						handleEditAction(EDIT_CLASSROOM_ACTION.saveComplete);
					})
					.catch((e) => {
						setHasError(e);
						setIsSaving(false);
					});
			} else {
				setHasError(t("classRename.nameExistsError"));
				setIsSaving(false);
				return;
			}
		} else if (personalisation.displayName === stagedPersonalisation.displayName) {
			setHasError(t("classRename.nameExistsError"));
		} else {
			handleEditAction(EDIT_CLASSROOM_ACTION.cancel);
		}
	};

	const handleEditAction = async (action) => {
		switch (action) {
			case EDIT_CLASSROOM_ACTION.cancel:
			case EDIT_CLASSROOM_ACTION.saveComplete:
				handleAction(action);
				break;
			case EDIT_CLASSROOM_ACTION.save:
				handleSave();
				break;
			default:
				break;
		}
	};

	const renderContent = () => {
		return (
			<div>
				<Box display={"flex"} flexDirection={"column"}>
					<label className="Classroom_title">{t("classRename.originalClassName")}</label>
					<label className="Classroom_description_background">{getClassroomOriginalName(classroom)}</label>
					<br />
					<label className="Classroom_title">{t("classRename.existingClassName")}</label>
					<label className="Classroom_description_background">{getClassroomDisplayName(classroom)}</label>
					<br />
					<label className="Classroom_title">{t("classRename.newClassName")}</label>
					<label className="Classroom_description">{t("classRename.newClassNameHint")}</label>
					<input
						className="form-input"
						placeholder={t("classRename.placeholder")}
						autoComplete="on"
						value={displayName}
						onChange={(e) => {
							setHasError(false);
							setDisplayName(e.target.value);
							setPersonalisation({ ...stagedPersonalisation, displayName: e.target.value });
						}}
						id="displayName"
						name="displayName"
					/>
					{hasError && <label className="Classroom_error">{hasError}</label>}
				</Box>
				<br />
				<div style={{ display: "flex", flexDirection: "row", alignItems: "start" }}>
					<Checkbox
						checked={stagedPersonalisation?.pinned}
						onChecked={(isChecked) => setPersonalisation({ ...stagedPersonalisation, pinned: isChecked })}
						label=""
					/>
					<div>
						<div className="ClassroomCreate_Pin_Container">
							<div className="ClassroomCreate_Pin">
								<IconPinClass />
							</div>
							<div>{t("classRename.pinClass")}</div>
						</div>
						<div className="ClassroomCreate_Pin_Container_Text_Secondary">{t("classRename.pinClassHint")}</div>
					</div>
				</div>
			</div>
		);
	};

	return (
		<Box onClick={(event) => event.stopPropagation()}>
			<StandardModalV2
				title={t("classRename.title")}
				actions={renderActions()}
				modal={true}
				open={open}
				close={() => handleEditAction(EDIT_CLASSROOM_ACTION.cancel)}
				bodyClass="RenameClass_Modal"
			>
				{renderContent()}
			</StandardModalV2>
		</Box>
	);
};

export default ClassroomRename;

ClassroomRename.propTypes = {
	classroom: PropTypes.object,
	open: PropTypes.bool,
	handleAction: PropTypes.func,
};
